import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Campaign } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useTenantKey } from '../../lib/useTenantKey';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { FormField, Input, Textarea } from '../../components/ui/Field';
import { useFieldId } from '../../components/ui/useFieldId';

/**
 * Create, edit, or VIEW a Campaign's authoring fields (name, description, messaging).
 * A new campaign starts in DRAFT; approval/release actions live on the detail page.
 *
 * The read-only mode is not cosmetic: the server refuses to update anything that isn't DRAFT or
 * REVISE (campaigns.service assertEditable), so presenting an editable form for a published
 * campaign offers a Save that can only fail.
 */
export function CampaignFormModal({
  open,
  onClose,
  campaign,
  readOnly = false,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** When set → edit that campaign; otherwise create a new one. */
  campaign?: Campaign | null;
  /** Show the fields without letting them be changed (a campaign the API won't accept edits for). */
  readOnly?: boolean;
  onSaved?: (campaign: Campaign) => void;
}) {
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [messaging, setMessaging] = useState('');
  const [error, setError] = useState<string | null>(null);

  const nameId = useFieldId('campaign-name');
  const descId = useFieldId('campaign-description');
  const msgId = useFieldId('campaign-messaging');

  // Reset the form whenever the modal (re)opens for a given campaign.
  useEffect(() => {
    if (!open) return;
    setName(campaign?.name ?? '');
    setDescription(campaign?.description ?? '');
    setMessaging(campaign?.messaging ?? '');
    setError(null);
  }, [open, campaign]);

  const save = useMutation({
    mutationFn: () => {
      const dto = {
        name: name.trim(),
        description: description.trim() || undefined,
        messaging: messaging.trim() || undefined,
      };
      return campaign ? api.updateCampaign(campaign.id, dto) : api.createCampaign(dto);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns', tenantKey] });
      if (campaign) void queryClient.invalidateQueries({ queryKey: ['campaign', tenantKey, saved.id] });
      onSaved?.(saved);
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Could not save the campaign.'),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={readOnly ? 'Campaign details' : campaign ? 'Edit campaign' : 'New campaign'}
      footer={
        readOnly ? (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              disabled={!name.trim()}
              onClick={() => {
                setError(null);
                save.mutate();
              }}
            >
              {campaign ? 'Save changes' : 'Create campaign'}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        <FormField label="Name" htmlFor={nameId}>
          <Input
            disabled={readOnly}
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Spring product launch"
            autoFocus
          />
        </FormField>
        <FormField label="Description" htmlFor={descId} hint="Optional — a short internal summary.">
          <Textarea
            disabled={readOnly}
            id={descId}
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this campaign about?"
          />
        </FormField>
        <FormField
          label="Messaging"
          htmlFor={msgId}
          hint="Optional — shared positioning applied to every post in the campaign."
        >
          <Textarea
            disabled={readOnly}
            id={msgId}
            rows={3}
            value={messaging}
            onChange={(e) => setMessaging(e.target.value)}
            placeholder="Key messages, tone, and angles for this campaign…"
          />
        </FormField>
        {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
      </div>
    </Modal>
  );
}
